import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { stocktakeSessionsService, sitesService, stocktakingSettingsService, sectionMasterItemsService } from '../../services';
import stocktakeOfflineService from '../../services/stocktakeOfflineService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Loader2, Trash2, Plus, AlertTriangle, Wine, Users, Minus, TrendingUp, TrendingDown,
} from 'lucide-react';
import { formatCurrency, DataSourceBadge } from './stockAuditUtils';

const ADJ_TYPES = [
  { key: 'wastage', label: 'Wastage', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  { key: 'allowance', label: 'Allowances', icon: Wine, color: 'text-purple-600', bg: 'bg-purple-50' },
  { key: 'management', label: 'Management', icon: Users, color: 'text-teal-600', bg: 'bg-teal-50' },
];

export const AdjustmentsPanel = forwardRef(function AdjustmentsPanel({ sessionId, status, siteId, sessionDate, isEditable, wizardMode, isOfflineMode, offlineData }, ref) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('wastage');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  // iter360b — Track whether we've successfully hydrated `entries` from the
  // server at least once. The autosave debounce must NEVER fire before this
  // is true, otherwise an uninitialised empty state can silently overwrite
  // the auditor's saved work on the server (the "adjustments disappeared
  // after some time" bug). We also track the last successfully-loaded count
  // — if our local state has 0 rows but the server had > 0 at load time,
  // we send `force_clear=true` so the backend wipe is intentional.
  const loadedFromServerRef = useRef(false);
  const lastLoadedCountRef = useRef(0);
  // 2026-04-24 — Management retail inputs: VAT rate (org) + site COS budget %.
  // Drives the inline "A ex VAT / B cost" preview chips on each Management row.
  const [managementVatRate, setManagementVatRate] = useState(20);
  const [managementCosPct, setManagementCosPct] = useState(null); // null means "not configured"
  // 2026-02 — Allowance/Management name selectors read ONLY from the
  // org-scoped, active Summary section_master_items list (one approved
  // name list, no free-text duplicate for new rows). Wastage keeps free
  // text — there is no approved Stocktake-side category for it (see D3:
  // Summary's separate "Adjustments" section has no Stocktake equivalent).
  const [allowanceMasterItems, setAllowanceMasterItems] = useState([]);
  const [managementMasterItems, setManagementMasterItems] = useState([]);
  const { toast } = useToast();
  const autoSaveRef = useRef(null);
  const handleSaveRef = useRef(null);
  const initialLoadRef = useRef(true);
  const editable = isEditable !== undefined ? isEditable : !['completed', 'locked'].includes(status);

  // Fetch the two inputs needed to derive A & B for the Management preview.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isOfflineMode && offlineData) {
        const vat = parseFloat(offlineData.org_stocktaking_settings?.default_vat_rate);
        const cos = parseFloat(offlineData.site?.budget_cost_of_sales_pct);
        if (!cancelled) {
          if (!Number.isNaN(vat) && vat >= 0) setManagementVatRate(vat);
          setManagementCosPct(Number.isFinite(cos) ? cos : null);
        }
        return;
      }
      try {
        const [stg, site] = await Promise.all([
          stocktakingSettingsService.get().catch(() => null),
          siteId ? sitesService.get(siteId).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const vat = parseFloat(stg?.default_vat_rate);
        if (!Number.isNaN(vat) && vat >= 0) setManagementVatRate(vat);
        const cos = parseFloat(site?.budget_cost_of_sales_pct);
        setManagementCosPct(Number.isFinite(cos) ? cos : null);
      } catch {
        /* keep defaults */
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, isOfflineMode, offlineData]);

  // 2026-02 — Load the approved Allowance/Management name lists once.
  // Skipped entirely offline (no cached master-data source in the offline
  // package today); the dropdown falls back to "no options" there rather
  // than inventing a name match. Read-only fetch — reuses the exact same
  // org-scoped, active-only endpoint the Summary wizard already uses.
  useEffect(() => {
    if (isOfflineMode) return;
    let cancelled = false;
    (async () => {
      try {
        const [a, m] = await Promise.all([
          sectionMasterItemsService.list('allowance', true),
          sectionMasterItemsService.list('management', true),
        ]);
        if (!cancelled) {
          setAllowanceMasterItems(a || []);
          setManagementMasterItems(m || []);
        }
      } catch {
        /* leave empty — dropdown shows "No approved names configured" */
      }
    })();
    return () => { cancelled = true; };
  }, [isOfflineMode]);

  const load = useCallback(async () => {
    if (isOfflineMode && offlineData) {
      const record = await stocktakeOfflineService.loadOfflinePackage(Number(sessionId));
      const localAdj = record?.local_changes?.adjustment_entries;
      const pkgAdj = offlineData.adjustment_entries || [];
      const items = (localAdj || pkgAdj).map(item => ({
        ...item,
        value: item.value ?? '',
        description: item.description ?? '',
        notes: item.notes ?? '',
        sign: parseFloat(item.value || 0) >= 0 ? 'positive' : 'negative',
      }));
      setEntries(items);
      setTotal(items.reduce((sum, i) => sum + (parseFloat(i.value) || 0), 0));
      setDataSource('package');
      loadedFromServerRef.current = true;
      lastLoadedCountRef.current = items.length;
      setLoading(false);
      return;
    }
    try {
      const res = await stocktakeSessionsService.getAdjustments(sessionId);
      const items = (res.items || []).map(item => ({
        ...item,
        sign: parseFloat(item.value || 0) >= 0 ? 'positive' : 'negative',
      }));
      setEntries(items);
      setTotal(res.total || 0);
      setDataSource('live-api');
      loadedFromServerRef.current = true;
      lastLoadedCountRef.current = items.length;
    } catch {
      // iter360b — Leave `loadedFromServerRef` false so autosave stays
      // gated. A failed load MUST NOT cascade into an empty-state save
      // that wipes adjustments on the server.
    } finally { setLoading(false); }
  }, [sessionId, isOfflineMode, offlineData]);

  useEffect(() => { load(); }, [load]);

  // Recalculate total when entries change
  useEffect(() => {
    setTotal(entries.reduce((sum, e) => sum + (parseFloat(e.value) || 0), 0));
  }, [entries]);

  const filtered = entries.filter(e => e.adjustment_type === activeTab);
  const positiveEntries = filtered.filter(e => e.sign === 'positive');
  const negativeEntries = filtered.filter(e => e.sign === 'negative');
  const typeTotal = filtered.reduce((sum, e) => sum + (parseFloat(e.value) || 0), 0);
  const activeType = ADJ_TYPES.find(t => t.key === activeTab);

  const addRow = (sign) => {
    setEntries(prev => [...prev, {
      adjustment_type: activeTab,
      sign,
      description: '',
      value: '',
      notes: '',
      master_item_id: null,
      // iter420i — Wastage rows default to "excluded from result" so
      // existing clients see no change. Operator opts in per row.
      include_in_result: false,
      _new: true, _key: Math.random()
    }]);
  };

  const removeRow = (idx) => {
    const allOfType = entries.filter(e => e.adjustment_type === activeTab);
    if (idx >= allOfType.length) return;
    const target = allOfType[idx];
    setEntries(prev => prev.filter(e => e !== target));
  };

  const updateRow = (idx, field, val) => {
    setEntries(prev => {
      const typeEntries = prev.filter(e => e.adjustment_type === activeTab);
      if (idx >= typeEntries.length) return prev;
      const entry = typeEntries[idx];

      const updates = { [field]: val };

      // When updating value, enforce sign
      if (field === 'value') {
        const numVal = parseFloat(val);
        if (!isNaN(numVal) && numVal !== 0) {
          if (entry.sign === 'negative' && numVal > 0) {
            updates.value = String(-Math.abs(numVal));
          } else if (entry.sign === 'positive' && numVal < 0) {
            updates.value = String(Math.abs(numVal));
          }
        }
      }

      return prev.map(e => e === entry ? { ...e, ...updates } : e);
    });
  };

  // 2026-02 — Choosing a name from the approved list sets BOTH the
  // crosswalk id (what the server validates + what Summary resolves
  // against) and a mirrored description (what every existing report/
  // display already reads). One atomic update so the two never drift
  // apart mid-render.
  const selectMasterItem = (idx, item) => {
    setEntries(prev => {
      const typeEntries = prev.filter(e => e.adjustment_type === activeTab);
      if (idx >= typeEntries.length) return prev;
      const entry = typeEntries[idx];
      return prev.map(e => e === entry ? { ...e, description: item.name, master_item_id: item.id } : e);
    });
  };

  const handleSave = async (showToast = true) => {
    // iter360b — Hard guard: never let an autosave (showToast=false) fire
    // before the panel has hydrated from the server at least once. An
    // uninitialised empty state must not be allowed to silently wipe saved
    // adjustments. Explicit user-triggered saves still go through so the
    // auditor can intentionally clear all rows.
    if (!showToast && !loadedFromServerRef.current) return;
    setSaving(true);
    try {
      const payload = entries.filter(e => {
        const hasValue = e.value && parseFloat(e.value) !== 0;
        const hasDesc = e.description && e.description.trim();
        return hasValue || hasDesc;
      }).map(e => ({
        adjustment_type: e.adjustment_type,
        description: e.description || null,
        // iter360b — Preserve 0-value rows that have a description. Previously
        // the backend dropped these silently; we now keep them so the auditor
        // sees their work on reload.
        value: e.value && parseFloat(e.value) !== 0 ? parseFloat(e.value) : 0,
        notes: e.notes || null,
        master_item_id: e.master_item_id || null,
        // iter420i — Per-row "include in result" toggle. Only meaningful
        // on wastage rows; backend coerces to false on other types so
        // a stray UI flag can't change allowance/management treatment.
        include_in_result: e.adjustment_type === 'wastage' ? !!e.include_in_result : false,
      }));
      // iter360b — when the user has explicitly cleared every row, signal
      // the backend that the wipe is intentional. Without this flag the
      // server now refuses to delete-all on an empty payload (safety net
      // against accidental autosave wipes).
      const forceClear = payload.length === 0 && lastLoadedCountRef.current > 0;
      if (isOfflineMode) {
        const result = await stocktakeOfflineService.saveLocalChanges(Number(sessionId), { adjustment_entries: payload });
        if (!result.success) throw new Error(result.error || 'Failed to save locally');
        if (showToast) toast({ title: 'Adjustments Saved Locally' });
      } else {
        await stocktakeSessionsService.saveAdjustments(sessionId, payload, forceClear);
        if (showToast) toast({ title: 'Adjustments Saved' });
        // Keep our "loaded count" reference in sync so subsequent autosaves
        // know whether a future empty payload would be a real clear.
        lastLoadedCountRef.current = payload.length;
        if (showToast) load();
      }
    } catch (err) {
      if (showToast) toast({ title: 'Error', description: err.response?.data?.detail || err.message || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  useImperativeHandle(ref, () => ({ save: handleSave }));

  handleSaveRef.current = handleSave;
  useEffect(() => {
    if (initialLoadRef.current) { initialLoadRef.current = false; return; }
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => { handleSaveRef.current?.(false); }, 2000);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  }, [entries]);

  if (loading) return <Card><CardContent className="py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading adjustments...</CardContent></Card>;

  // Get the flat index within the full entries array for a given type entry
  const getTypeIdx = (entry) => {
    const typeEntries = entries.filter(e => e.adjustment_type === activeTab);
    return typeEntries.indexOf(entry);
  };

  const renderEntryRow = (entry, typeIdx) => {
    // 2026-04-24 — On the Management tab, derive A (ex-VAT retail) and B
    // (cost of sales) from the user's inc-VAT input so the auditor sees
    // the engine output live as they type. Chips mirror the math in
    // stock_audit_engine.py and localCalculationEngine.js.
    const isMgmt = activeTab === 'management';
    let mgmtA = null;
    let mgmtB = null;
    let mgmtCosMissing = false;
    if (isMgmt) {
      const rawIncl = parseFloat(entry.value);
      if (Number.isFinite(rawIncl) && rawIncl !== 0) {
        const divisor = 1 + ((managementVatRate || 0) / 100);
        mgmtA = divisor > 0 ? rawIncl / divisor : rawIncl;
        if (managementCosPct != null && Number.isFinite(managementCosPct)) {
          mgmtB = mgmtA * (managementCosPct / 100);
        } else {
          mgmtCosMissing = true;
        }
      }
    }
    // 2026-02 — Allowance/Management name selectors read ONLY from the
    // approved, org-scoped, active section_master_items list. Shown for:
    // a brand-new unsaved row, OR a previously-saved row that already
    // carries a master_item_id (selected via this same dropdown before).
    // A genuinely historical free-text row (saved before this feature,
    // master_item_id still null) keeps its original free-text input
    // untouched — never force-converted.
    const catalogItems = activeTab === 'allowance' ? allowanceMasterItems : activeTab === 'management' ? managementMasterItems : null;
    const usesCatalog = !!catalogItems && (entry._new || entry.master_item_id != null);
    return (
      <div key={entry.id || entry._key || typeIdx} className="space-y-1" data-testid={`adj-row-${activeTab}-${typeIdx}`}>
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            {usesCatalog ? (
              <Select
                value={entry.master_item_id ? String(entry.master_item_id) : ''}
                onValueChange={(val) => {
                  const item = catalogItems.find(i => String(i.id) === val);
                  if (item) selectMasterItem(typeIdx, item);
                }}
                disabled={!editable}
              >
                <SelectTrigger className="h-9" data-testid={`adj-desc-select-${activeTab}-${typeIdx}`}>
                  <SelectValue placeholder={catalogItems.length ? 'Select approved name…' : 'No approved names configured'} />
                </SelectTrigger>
                <SelectContent>
                  {catalogItems.map(item => (
                    <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                placeholder="Description (e.g. staff drinks, breakage, promotion)"
                value={entry.description || ''}
                onChange={(e) => updateRow(typeIdx, 'description', e.target.value)}
                disabled={!editable}
                className="h-9"
                data-testid={`adj-desc-${activeTab}-${typeIdx}`}
              />
            )}
          </div>
          <div className="w-32">
            <Input
              type="number" step="0.01"
              placeholder={entry.sign === 'negative' ? '-0.00' : '0.00'}
              value={entry.value || ''}
              onChange={(e) => updateRow(typeIdx, 'value', e.target.value)}
              disabled={!editable}
              className={`h-9 font-medium ${entry.sign === 'negative' ? 'text-red-600' : 'text-emerald-600'}`}
              data-testid={`adj-value-${activeTab}-${typeIdx}`}
            />
          </div>
          <div className="w-40">
            <Input
              placeholder="Note (optional)"
              value={entry.notes || ''}
              onChange={(e) => updateRow(typeIdx, 'notes', e.target.value)}
              disabled={!editable}
              className="h-9 text-xs"
              data-testid={`adj-notes-${activeTab}-${typeIdx}`}
            />
          </div>
          {editable && (
            <Button size="icon" variant="ghost" className="h-9 w-9 text-red-400 hover:text-red-600 shrink-0"
              onClick={() => removeRow(typeIdx)} data-testid={`adj-delete-${activeTab}-${typeIdx}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {/* iter420i — Per-row "Include in result" toggle for wastage.
            Default OFF preserves the historical KPI-only treatment.
            When ON, the value is added back to the final stocktake
            result the same way allowances are — operator opt-in for
            clients who want recorded loss to reduce the measured
            deficit. Hidden on allowance/management rows (their
            treatment is fixed: allowances always count, management
            always doesn't). */}
        {activeTab === 'wastage' && (
          <div className="flex items-center gap-2 pl-1 pt-0.5" data-testid={`wastage-include-row-${typeIdx}`}>
            <Switch
              checked={!!entry.include_in_result}
              onCheckedChange={(v) => updateRow(typeIdx, 'include_in_result', !!v)}
              disabled={!editable}
              id={`wastage-include-${typeIdx}`}
              data-testid={`wastage-include-toggle-${typeIdx}`}
            />
            <label
              htmlFor={`wastage-include-${typeIdx}`}
              className={`text-[11px] cursor-pointer select-none ${entry.include_in_result ? 'text-emerald-700 font-medium' : 'text-muted-foreground'}`}
            >
              {entry.include_in_result
                ? 'Included in result — value adds back to the stocktake result'
                : 'Excluded from result — reported for information only (KPI)'}
            </label>
          </div>
        )}
        {isMgmt && mgmtA !== null && (
          <div className="flex gap-1.5 items-center pl-1 text-[11px] tabular-nums flex-wrap" data-testid={`mgmt-preview-${typeIdx}`}>
            <span
              className="inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 px-2 py-0.5"
              title={`Retail inc VAT ÷ (1 + ${Number(managementVatRate).toFixed(0)}%/100)`}
              data-testid={`mgmt-preview-a-${typeIdx}`}
            >
              <span className="font-semibold">Retail ex VAT</span>
              <span className="text-teal-600">({Number(managementVatRate).toFixed(0)}% VAT)</span>
              <span className="font-semibold">{formatCurrency(mgmtA)}</span>
            </span>
            {mgmtCosMissing ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 text-amber-800 px-2 py-0.5"
                title="Set the Beverages COS Budget % on the Site Master to derive the Cost of Sales"
                data-testid={`mgmt-preview-b-missing-${typeIdx}`}
              >
                <AlertTriangle className="h-3 w-3" />
                <span className="font-semibold">Cost of Sales</span>
                <span>COS Budget % not configured</span>
              </span>
            ) : (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${mgmtB >= 0 ? 'bg-indigo-50 border-indigo-200 text-indigo-800' : 'bg-red-50 border-red-200 text-red-800'}`}
                title={`Retail ex VAT × ${Number(managementCosPct).toFixed(2)}% COS Budget`}
                data-testid={`mgmt-preview-b-${typeIdx}`}
              >
                <span className="font-semibold">Cost of Sales</span>
                <span className="text-indigo-600">({Number(managementCosPct).toFixed(2)}% COS)</span>
                <span className="font-semibold">{formatCurrency(mgmtB)}</span>
              </span>
            )}
            <span className="text-[10px] text-muted-foreground italic">
              Retail ex VAT is subtracted from the final result · Cost of Sales is added to the total Cost of Sales
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="adjustments-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Minus className="h-5 w-5 text-slate-600" />
            Adjustments
            {dataSource && <DataSourceBadge source={dataSource} />}
          </CardTitle>
          <div className="text-sm font-medium">
            Net: <span className={total >= 0 ? 'text-emerald-600' : 'text-red-600'}>{formatCurrency(total)}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Tabs */}
        <div className="flex gap-1 mb-3 border-b">
          {ADJ_TYPES.map(t => {
            const typeCount = entries.filter(e => e.adjustment_type === t.key).length;
            return (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === t.key ? `border-slate-900 ${t.color}` : 'border-transparent text-muted-foreground hover:text-foreground'
                }`} data-testid={`adj-tab-${t.key}`}>
                {t.label} {typeCount > 0 && <Badge variant="secondary" className="ml-1 text-xs">{typeCount}</Badge>}
              </button>
            );
          })}
        </div>

        {/* Type summary */}
        <div className={`p-3 ${activeType?.bg || 'bg-slate-50'} rounded-lg mb-3`}>
          <p className="text-xs text-muted-foreground">
            {activeType?.label} Net Total
            {activeTab === 'management' && (
              <span className="ml-1 text-[10px] uppercase tracking-wide text-teal-700 font-semibold">(Retail Sales inc VAT)</span>
            )}
          </p>
          <p className={`text-lg font-bold ${typeTotal >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`adj-${activeTab}-total`}>{formatCurrency(typeTotal)}</p>
          {activeTab === 'management' && (() => {
            const divisor = 1 + ((managementVatRate || 0) / 100);
            const aggA = divisor > 0 ? typeTotal / divisor : typeTotal;
            const aggB = (managementCosPct != null && Number.isFinite(managementCosPct))
              ? aggA * (managementCosPct / 100)
              : null;
            const hasValues = typeTotal !== 0;
            return (
              <>
                {hasValues && (
                  <div className="mt-2 flex gap-2 flex-wrap text-[11px] tabular-nums" data-testid="management-aggregate-preview">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-white border border-teal-300 px-2 py-1">
                      <span className="font-semibold text-teal-700">Retail ex VAT</span>
                      <span className="text-teal-600">({Number(managementVatRate).toFixed(0)}%)</span>
                      <span className="font-bold text-slate-900" data-testid="management-total-a">{formatCurrency(aggA)}</span>
                    </span>
                    {aggB != null ? (
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-white border border-indigo-300 px-2 py-1">
                        <span className="font-semibold text-indigo-700">Cost of Sales</span>
                        <span className="text-indigo-600">({Number(managementCosPct).toFixed(2)}% COS budget)</span>
                        <span className="font-bold text-slate-900" data-testid="management-total-b">{formatCurrency(aggB)}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-white border border-amber-300 px-2 py-1 text-amber-800">
                        <AlertTriangle className="h-3 w-3" />
                        <span className="font-semibold">Cost of Sales unavailable</span>
                        <span>— configure Site COS Budget %</span>
                      </span>
                    )}
                  </div>
                )}
                <p className="mt-1 text-[11px] text-teal-800 leading-snug" data-testid="management-retail-hint">
                  Enter the <strong>retail sales value inclusive of VAT</strong>. The
                  system strips VAT (using the org's default VAT rate) to get the
                  <em> Retail ex VAT</em>, and applies the site's COS Budget % to
                  get the <em>Management Cost of Sales</em>. The Retail ex VAT is
                  subtracted from the final surplus/shortage, and the Cost of Sales
                  flows into the total Cost of Sales.
                </p>
              </>
            );
          })()}
        </div>

        {/* Column headers */}
        <div className="text-xs text-muted-foreground font-medium px-1 flex gap-2 mb-2">
          <div className="flex-1">{activeTab === 'wastage' ? 'Description' : 'Approved Name'}</div>
          <div className="w-32">
            {activeTab === 'management' ? `Retail inc VAT (${'\u00A3'})` : `Amount (${'\u00A3'})`}
          </div>
          <div className="w-40">Note</div>
          {editable && <div className="w-9"></div>}
        </div>

        {/* Negative adjustments section (deductions) */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="h-4 w-4 text-red-500" />
            <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">Negative Adjustments</span>
            <span className="text-xs text-muted-foreground">(increase expected closing stock)</span>
          </div>
          <div className="space-y-2 pl-1 border-l-2 border-red-200 ml-1">
            {negativeEntries.length === 0 && (
              <p className="text-xs text-muted-foreground py-2 pl-3">No negative adjustments</p>
            )}
            {negativeEntries.map((entry) => renderEntryRow(entry, getTypeIdx(entry)))}
          </div>
          {editable && (
            <Button size="sm" variant="outline" className="mt-2 ml-3 text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => addRow('negative')} data-testid={`add-adj-negative-${activeTab}-btn`}>
              <Plus className="h-3 w-3 mr-1" />Add Negative
            </Button>
          )}
        </div>

        {/* Positive adjustments section (additions) — iter426
            (Iteration B #10): Management tab does NOT support positive
            adjustments. Management entries always reduce expected
            closing (staff drinks, comps, spillage), never add to it. */}
        {activeTab !== 'management' && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
            <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Positive Adjustments</span>
            <span className="text-xs text-muted-foreground">(reduce expected closing stock)</span>
          </div>
          <div className="space-y-2 pl-1 border-l-2 border-emerald-200 ml-1">
            {positiveEntries.length === 0 && (
              <p className="text-xs text-muted-foreground py-2 pl-3">No positive adjustments</p>
            )}
            {positiveEntries.map((entry) => renderEntryRow(entry, getTypeIdx(entry)))}
          </div>
          {editable && (
            <Button size="sm" variant="outline" className="mt-2 ml-3 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              onClick={() => addRow('positive')} data-testid={`add-adj-positive-${activeTab}-btn`}>
              <Plus className="h-3 w-3 mr-1" />Add Positive
            </Button>
          )}
        </div>
        )}

        {!wizardMode && editable && (
          <div className="flex gap-2 mt-3 pt-3 border-t">
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-adjustments-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Save All Adjustments
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
